import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toastWarning } from 'utils/toast';
import { encryptURL } from 'utils/functions';
import { RelatoriosContext } from 'contexts/RelatoriosContext';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { OrçamentosContext } from 'contexts/OrçamentosContext';
import { ServicosContext } from 'contexts/ServicosContext';
import { VendaContext } from 'contexts/VendaContext';
import { DropsContext } from 'contexts/DropsContext';
import { GridContext } from 'contexts/GridContext';
import { AuthContext } from 'contexts/AuthContext';
import { useDialog, useModal } from 'components/Modals';
import JustificativaModal from 'components/Modals/JustificativaModal';
import MaletaModal from 'components/Modals/MaletaModal';
import ResumoModal from 'components/Modals/ResumoModal';
import ContatoModal from 'components/Modals/ContatoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import getServicosFilterFields from './filterFields';
import columnOverrides from './columnOverrides';

const Servicos = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rotina = 'Servicos';
  const titulo = 'Ordens de Serviço';
  const { openModal, closeModal } = useModal();
  const { openDialog, closeDialog } = useDialog();
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { contas } = useContext(VendaContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { getURLRelatorio } = useContext(RelatoriosContext);
  const { postStatus, postLoading } = useContext(OrçamentosContext);
  const { postDocumento, postLoading: docLoad } = useContext(DocumentosContext);
  const { servicos, getLoading, getServicos, exportOrdens } =
    useContext(ServicosContext);
  const filterFields = useMemo(() => getServicosFilterFields(drops), [drops]);
  const loading = getLoading || postLoading || docLoad;

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();
    closeModal();
    closeDialog();
    getServicos({ ...props, rotina, filter });
  };

  const changeStatus = ({ documento_id, status_id, motivo }) => {
    const data = { Registros: [{ documento_id, status_id, motivo }] };
    postDocumento({ data, cb: loadGrid });
  };

  const toolbarActions = [
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getValues(), order: servicos?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getValues(), order: servicos.order }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={servicos?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
    {
      name: 'Adicionar Ordem de Serviço',
      icon: 'add',
      placement: 'primary',
      action: () => navigate('/app/Servicos/Gerar'),
      show: !Boolean(user?.permissoes?.block_gerar_servico),
    },
    {
      name: 'Ordens',
      icon: 'print',
      action: (selected) => {
        if (Boolean(selected?.length)) {
          const data = selected?.map((s) => s?.id);
          exportOrdens({ data, cb: loadGrid });
        } else {
          return toastWarning('Selecione ao menos um item');
        }
      },
    },
  ];

  const rowActions = [
    {
      name: 'Em execução',
      icon: 'schedule',
      color: 'primary.main',
      show: ({ row }) => Boolean(row?.executantes),
    },
    {
      name: 'Detalhes',
      icon: 'visibility',
      action: ({ row }) => navigate(`/app/Documento/${row?.id}`),
    },
    {
      name: 'Agendamento',
      icon: 'calendar_month',
      menu: true,
      show: ({ row }) =>
        !Boolean(user?.permissoes?.block_agendamento_servico) &&
        [16]?.includes(row?.status_id),
      action: ({ row }) =>
        changeStatus({ documento_id: row?.id, status_id: 22 }),
    },
    {
      name: 'Apontamento de Horas',
      icon: 'schedule',
      menu: true,
      show: ({ row }) =>
        !Boolean(user?.permissoes?.block_apontamento_servico) &&
        [45]?.includes(row?.status_id),
      action: ({ row }) =>
        navigate(`/app/Servicos/Apontamento/${encryptURL(row)}`),
    },
    {
      name: 'Direcionamento Técnico',
      icon: 'build',
      menu: true,
      show: ({ row }) =>
        !Boolean(user?.permissoes?.block_direcionamento_tecnico) &&
        user?.nivel !== 'TÉCNICO' &&
        [16, 20, 21, 22, 30, 45]?.includes(row?.status_id),
      action: ({ row }) =>
        navigate(`/app/Servicos/Direcionamento/${encryptURL(row)}`),
    },
    {
      name: 'Concluir Serviço',
      icon: 'check',
      menu: true,
      show: ({ row }) =>
        !Boolean(user?.permissoes?.block_conclusao_servico) &&
        row?.status === 'PENDENTE' &&
        (![20]?.includes(row?.status_id) ||
          ([20]?.includes(row?.status_id) && row?.manutencao === 'GARANTIA')),
      action: ({ row }) => {
        if (
          user?.configuracoes?.Servico?.localizacao_equipamento_obrigatoria &&
          Boolean(row?.equipamento_id) &&
          (row?.qtlocais || 0) < 2
        ) {
          return toastWarning('Localização do equipamento não informada');
        }
        if (row?.status_id === 20) {
          changeStatus({ documento_id: row?.id, status_id: 6 });
        } else {
          postStatus({
            data: {
              documento_id: row?.id,
              status_id: row?.status_id === 28 ? 28 : 20,
            },
            cb: loadGrid,
          });
        }
      },
    },
    {
      name: 'Descartar Equipamento',
      icon: 'delete',
      menu: true,
      show: ({ row }) =>
        Boolean(row?.equipamento_id) &&
        !Boolean(user?.permissoes?.block_descarte_equipamento) &&
        ![20]?.includes(row?.status_id),
      action: ({ row }) =>
        changeStatus({ documento_id: row?.id, status_id: 27 }),
    },
    {
      name: 'Entregar Equipamento',
      icon: 'check',
      menu: true,
      show: ({ row }) =>
        Boolean(row?.equipamento_id) &&
        !Boolean(user?.permissoes?.block_entrega_equipamento) &&
        [26]?.includes(row?.status_id),
      action: ({ row }) =>
        postStatus({
          data: {
            documento_id: row?.id,
            status_id: 6,
            motivo: 'Equipamento Entregue',
          },
          cb: loadGrid,
        }),
    },
    {
      name: 'Cancelar Serviço',
      icon: 'cancel',
      menu: true,
      show: ({ row }) =>
        !Boolean(user?.permissoes?.block_cancelamento_servico) &&
        row?.status === 'PENDENTE',
      action: ({ row }) =>
        openDialog(
          <JustificativaModal
            onSubmit={({ motivo, reabertura }) =>
              changeStatus({
                documento_id: row?.id,
                status_id: 2,
                motivo,
                reabertura,
              })
            }
          />,
          'Cancelar Serviço'
        ),
    },
    {
      name: 'Resumo da Ordem de Serviço',
      icon: 'description',
      menu: true,
      show: () => !Boolean(user?.permissoes?.block_resumo_servico),
      action: ({ row }) => navigate(`/app/Servicos/Resumo/${encryptURL(row)}`),
    },
    {
      name: 'Faturar',
      icon: 'attach_money',
      menu: true,
      show: ({ row }) =>
        !Boolean(user?.permissoes?.block_faturamento_servico) &&
        user?.nivel !== 'TÉCNICO' &&
        [20, 45]?.includes(row?.status_id),
      action: ({ id }) => {
        const hash = encryptURL({ id });
        if (Boolean(contas?.cxconta_id) && !Boolean(contas?.seqcaixa)) {
          return toastWarning('Você deve abrir o caixa para continuar');
        }
        navigate(`/app/Servicos/Faturamento/${hash}`);
      },
    },
    {
      name: 'Imprimir Ordem de Serviço',
      icon: 'print',
      menu: true,
      action: ({ row }) =>
        getURLRelatorio({
          data: { codigo: 'OS00000001', documento_id: row?.id },
          cb: loadGrid,
        }),
    },
    {
      name: 'Imprimir Etiqueta de Equipamento',
      icon: 'print',
      menu: true,
      show: ({ row }) => Boolean(row?.equipamento_id),
      action: ({ row }) =>
        getURLRelatorio({
          data: { codigo: 'ETQOS00001', ids: [row?.id] },
          cb: loadGrid,
        }),
    },
    {
      name: 'Registrar Contato',
      icon: 'contact_phone',
      menu: true,
      action: ({ row }) =>
        openDialog(<ContatoModal item={row} />, 'Registrar Contato'),
    },
    {
      name: 'Inserir Itens da Maleta',
      icon: 'business_center',
      menu: true,
      show: ({ row }) => row?.status === 'PENDENTE',
      action: ({ row }) =>
        openDialog(<MaletaModal row={row} />, 'Inserir Itens da Maleta'),
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} titulo={titulo} colunas={servicos?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={servicos}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={loading}
          loadGrid={loadGrid}
          getRowHeight={() => 'auto'}
        />
      </Card>
    </Container>
  );
};

export default Servicos;
